import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  ClipboardPaste,
  Download,
  FileSpreadsheet,
  Link2,
  Loader2,
  Sparkles,
  UploadCloud,
  X,
} from "lucide-react";
import { getPipelines } from "../../CachedData";
import useLeadImport from "./useLeadImport";
import { LEAD_IMPORT_FIELDS } from "./leadImportUtils";

const SOURCE_OPTIONS = [
  { key: "file", label: "Upload file", detail: "CSV, Excel or TSV", icon: FileSpreadsheet },
  { key: "google", label: "Google Sheet", detail: "Paste a public link", icon: Link2 },
  { key: "paste", label: "Paste rows", detail: "Copy from any sheet", icon: ClipboardPaste },
];

const STEP_LABELS = [
  { key: "source", label: "Choose source" },
  { key: "map", label: "Map fields" },
  { key: "result", label: "Review result" },
];

const SAMPLE_CSV = [
  "Name,Email,Phone,Company,Designation,Location,Lead Source,Priority Area",
  "Ada Lovelace,ada@example.com,+15550102030,Analytical Engines,Founder,London,Website,Automation",
  "Alan Turing,alan@example.com,+15550607080,Bletchley,Director,Manchester,Referral,CRM",
].join("\n");

function downloadSampleCsv() {
  const blob = new Blob([SAMPLE_CSV], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "auto-appy-leads-sample.csv";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function StepHeader({ current }) {
  const currentIndex = STEP_LABELS.findIndex(({ key }) => key === current);

  return (
    <div className="grid grid-cols-3 border-b border-[#dcebf3] bg-[#f8fcfe] px-6 sm:px-8">
      {STEP_LABELS.map((step, index) => {
        const complete = index < currentIndex;
        const active = index === currentIndex;
        return (
          <div key={step.key} className="relative flex items-center gap-2.5 py-4">
            <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${
              complete || active ? "bg-[#1689c1] text-white" : "bg-white text-[#7892a4] ring-1 ring-[#cfe1eb]"
            }`}>
              {complete ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : index + 1}
            </span>
            <span className={`hidden text-sm font-semibold sm:block ${active ? "text-[#123b56]" : "text-[#7892a4]"}`}>
              {step.label}
            </span>
            {active && <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-[#1689c1]" />}
          </div>
        );
      })}
    </div>
  );
}

function SourceStep({ loading, loadFile, loadGoogleSheet, loadPaste }) {
  const [source, setSource] = useState("file");
  const [sheetUrl, setSheetUrl] = useState("");
  const [pastedRows, setPastedRows] = useState("");
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef(null);

  const acceptFile = (file) => {
    if (file) loadFile(file);
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2 text-[#1689c1]">
          <Sparkles className="h-4 w-4" />
          <span className="text-xs font-bold uppercase tracking-[0.14em]">Bring your leads in</span>
        </div>
        <h3 className="mt-2 text-2xl font-bold tracking-tight text-[#102f44]">Where is your lead data?</h3>
        <p className="mt-1 text-sm leading-6 text-[#637e91]">
          Your first row should contain column names. You will review and map every field before anything is saved.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        {SOURCE_OPTIONS.map((option) => {
          const Icon = option.icon;
          const selected = source === option.key;
          return (
            <button
              key={option.key}
              type="button"
              onClick={() => setSource(option.key)}
              className={`flex items-center gap-3 rounded-2xl border p-4 text-left transition ${
                selected
                  ? "border-[#49add8] bg-[#eef9fd] shadow-[0_8px_24px_-18px_rgba(20,125,181,0.8)]"
                  : "border-[#dbe8ef] bg-white hover:border-[#a8cede] hover:bg-[#fbfdfe]"
              }`}
            >
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${selected ? "bg-[#1689c1] text-white" : "bg-[#edf5f8] text-[#58788d]"}`}>
                <Icon className="h-5 w-5" />
              </span>
              <span>
                <span className="block text-sm font-bold text-[#17384e]">{option.label}</span>
                <span className="mt-0.5 block text-xs text-[#7892a4]">{option.detail}</span>
              </span>
            </button>
          );
        })}
      </div>

      {source === "file" && (
        <div>
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              acceptFile(event.dataTransfer.files?.[0]);
            }}
            disabled={loading}
            className={`flex w-full flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-12 text-center transition ${
              dragging ? "border-[#1689c1] bg-[#eef9fd]" : "border-[#cfe1eb] bg-[#fbfdfe] hover:border-[#72bad8] hover:bg-[#f6fbfd]"
            }`}
          >
            {loading ? <Loader2 className="h-8 w-8 animate-spin text-[#1689c1]" /> : <UploadCloud className="h-8 w-8 text-[#1689c1]" />}
            <span className="mt-3 text-base font-bold text-[#17384e]">{loading ? "Reading your file…" : "Drop a file here or choose from your computer"}</span>
            <span className="mt-1 text-sm text-[#7892a4]">CSV, XLSX, XLS, TSV, ODS or TXT · up to 5,000 rows</span>
          </button>
          <input
            ref={fileInput}
            type="file"
            accept=".csv,.xlsx,.xls,.tsv,.ods,.xlsb,.txt,text/csv"
            className="hidden"
            onChange={(event) => {
              acceptFile(event.target.files?.[0]);
              event.target.value = "";
            }}
          />
          <button type="button" onClick={downloadSampleCsv} className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[#147db5] hover:text-[#0d5f8e]">
            <Download className="h-4 w-4" /> Download sample CSV
          </button>
        </div>
      )}

      {source === "google" && (
        <div className="rounded-2xl border border-[#dbe8ef] bg-[#fbfdfe] p-5">
          <label htmlFor="lead-google-sheet-url" className="text-sm font-bold text-[#17384e]">Public Google Sheet link</label>
          <div className="mt-2 flex flex-col gap-3 sm:flex-row">
            <input
              id="lead-google-sheet-url"
              type="url"
              value={sheetUrl}
              onChange={(event) => setSheetUrl(event.target.value)}
              placeholder="https://docs.google.com/spreadsheets/d/…"
              className="min-w-0 flex-1 rounded-xl border border-[#cfe1eb] bg-white px-4 py-3 text-sm text-[#17384e] outline-none transition placeholder:text-[#9aafbd] focus:border-[#49add8] focus:ring-4 focus:ring-[#dff3fb]"
            />
            <button
              type="button"
              onClick={() => loadGoogleSheet(sheetUrl)}
              disabled={loading || !sheetUrl.trim()}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#147db5] px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-[#106a99] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              Load sheet
            </button>
          </div>
          <p className="mt-3 text-sm leading-6 text-[#637e91]">
            In Google Sheets choose <strong>Share</strong>, set General access to <strong>Anyone with the link</strong>, then paste the link here. The selected sheet tab is respected.
          </p>
        </div>
      )}

      {source === "paste" && (
        <div>
          <label htmlFor="lead-pasted-rows" className="text-sm font-bold text-[#17384e]">Paste spreadsheet rows</label>
          <textarea
            id="lead-pasted-rows"
            value={pastedRows}
            onChange={(event) => setPastedRows(event.target.value)}
            rows={9}
            placeholder={"Name\tEmail\tPhone\tCompany\nAda Lovelace\tada@example.com\t+15550102030\tAnalytical Engines"}
            className="mt-2 w-full resize-none rounded-2xl border border-[#cfe1eb] bg-[#fbfdfe] px-4 py-3 font-mono text-sm leading-6 text-[#17384e] outline-none transition placeholder:text-[#9aafbd] focus:border-[#49add8] focus:bg-white focus:ring-4 focus:ring-[#dff3fb]"
          />
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={() => loadPaste(pastedRows)}
              disabled={!pastedRows.trim()}
              className="inline-flex items-center gap-2 rounded-xl bg-[#147db5] px-5 py-2.5 text-sm font-bold text-white transition hover:bg-[#106a99] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Continue to mapping <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MappingStep({ columns, rows, mapping, sourceLabel, totalRows, truncated, pipelineName, setFieldMapping, customFields = [], starterFields }) {
  const mappedColumns = useMemo(() => new Set(Object.values(mapping).filter(Boolean)), [mapping]);
  const previewColumns = columns.slice(0, 5);
  const importFields = useMemo(() => [
    ...(starterFields ? [LEAD_IMPORT_FIELDS[0], ...starterFields.map((field) => ({ key: field.key, label: field.label, group: field.section, required: field.is_required }))] : LEAD_IMPORT_FIELDS),
    ...customFields.map((field) => ({
      key: `custom:${field.key}`,
      label: field.label,
      required: field.is_required && (field.default_value === null || field.default_value === undefined || field.default_value === ""),
      group: field.section || "Custom field",
    })),
  ], [customFields, starterFields]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 rounded-2xl border border-[#cfe8f3] bg-[#f2faff] p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-bold text-[#17384e]">{totalRows.toLocaleString()} lead row{totalRows === 1 ? "" : "s"} ready to map</p>
          <p className="mt-1 text-xs text-[#637e91]">
            From {sourceLabel} · {pipelineName
              ? `New leads enter the first active stage of ${pipelineName}.`
              : "No pipeline yet—leads will be saved to your workspace without a stage."}
          </p>
        </div>
        {truncated && <span className="w-fit rounded-full bg-[#fff1d6] px-3 py-1 text-xs font-bold text-[#9a5a00]">First 5,000 rows only</span>}
      </div>

      <div>
        <h3 className="text-xl font-bold tracking-tight text-[#102f44]">Match your columns to CRM fields</h3>
        <p className="mt-1 text-sm text-[#637e91]">Lead name is required. Unmapped columns are ignored and nothing is saved until you import.</p>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {importFields.map((field) => {
          const missing = field.required && !mapping[field.key];
          return (
            <label key={field.key} className={`rounded-xl border p-3 transition ${missing ? "border-[#f2b8b5] bg-[#fffafa]" : "border-[#dbe8ef] bg-white"}`}>
              <span className="flex items-center justify-between gap-3">
                <span className="text-sm font-bold text-[#17384e]">
                  {field.label}{field.required && <span className="ml-1 text-[#d6493f]">*</span>}
                </span>
                <span className="text-[11px] font-semibold uppercase tracking-wide text-[#8aa0af]">{field.group}</span>
              </span>
              <select
                value={mapping[field.key] || ""}
                onChange={(event) => setFieldMapping(field.key, event.target.value)}
                className="mt-2 w-full rounded-lg border border-[#cfe1eb] bg-[#fbfdfe] px-3 py-2.5 text-sm font-medium text-[#34566d] outline-none focus:border-[#49add8] focus:ring-3 focus:ring-[#dff3fb]"
              >
                <option value="">Do not import</option>
                {columns.map((column) => (
                  <option
                    key={column}
                    value={column}
                    disabled={mappedColumns.has(column) && mapping[field.key] !== column}
                  >
                    {column}
                  </option>
                ))}
              </select>
            </label>
          );
        })}
      </div>

      <div>
        <p className="mb-2 text-xs font-bold uppercase tracking-[0.12em] text-[#7892a4]">Source preview</p>
        <div className="max-w-full overflow-x-auto rounded-2xl border border-[#dbe8ef]">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-[#f3f8fb] text-[#45657a]">
              <tr>{previewColumns.map((column) => <th key={column} className="whitespace-nowrap px-4 py-3 font-bold">{column}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-[#e5eef3] bg-white">
              {rows.slice(0, 4).map((row, index) => (
                <tr key={index}>{previewColumns.map((column) => <td key={column} className="max-w-56 truncate whitespace-nowrap px-4 py-3 text-[#516f82]">{row[column] || "—"}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ResultStep({ result }) {
  const stats = [
    { label: "Created", value: result?.created || 0, tone: "text-[#137b57] bg-[#e7f8f1]" },
    { label: "Updated", value: result?.updated || 0, tone: "text-[#147db5] bg-[#e9f6fc]" },
    { label: "Skipped", value: result?.skipped || 0, tone: "text-[#6b7280] bg-[#f1f5f7]" },
    { label: "Needs review", value: result?.failed || 0, tone: "text-[#b5473d] bg-[#fff0ee]" },
  ];

  return (
    <div className="space-y-7 py-2">
      <div className="text-center">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#e3f7ef] text-[#13845d] shadow-sm"><CheckCircle2 className="h-8 w-8" /></span>
        <h3 className="mt-4 text-2xl font-bold tracking-tight text-[#102f44]">Lead import complete</h3>
        {result?.stage ? (
          <p className="mt-1 text-sm text-[#637e91]">
            New leads were placed in <strong>{result.stage.name}</strong> of {result.pipeline?.name || "your selected pipeline"}.
          </p>
        ) : (
          <p className="mt-1 text-sm text-[#637e91]">
            Your leads are now in the workspace. Create a pipeline when you are ready to organise their stages.
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-2xl border border-[#dbe8ef] bg-white p-4 text-center">
            <span className={`mx-auto flex h-10 min-w-10 w-fit items-center justify-center rounded-xl px-3 text-lg font-bold ${stat.tone}`}>{stat.value}</span>
            <p className="mt-2 text-sm font-semibold text-[#516f82]">{stat.label}</p>
          </div>
        ))}
      </div>

      {result?.errors?.length > 0 && (
        <div className="rounded-2xl border border-[#f2c8c4] bg-[#fffafa] p-4">
          <p className="flex items-center gap-2 text-sm font-bold text-[#a33d35]"><AlertCircle className="h-4 w-4" /> Rows that need attention</p>
          <div className="mt-3 max-h-36 space-y-2 overflow-y-auto text-sm text-[#7f514d]">
            {result.errors.map((item) => <p key={`${item.row}-${item.message}`}><strong>Row {item.row}:</strong> {item.message}</p>)}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ImportLeadsModal({ workspaceId, pipelineId, customFields = [], starterFields, schemaError, onClose, onImported }) {
  const [pipelineName, setPipelineName] = useState("");
  const contentRef = useRef(null);
  const importer = useLeadImport({ workspaceId, pipelineId, customFields, starterFields });
  const missingRequiredStarterField = starterFields?.some((field) => field.is_required && !importer.mapping[field.key]);
  const missingRequiredCustomField = customFields.some((field) => field.is_required && (field.default_value === null || field.default_value === undefined || field.default_value === "") && !importer.mapping[`custom:${field.key}`]);

  useEffect(() => {
    getPipelines(workspaceId)
      .then((pipelines) => {
        const match = (Array.isArray(pipelines) ? pipelines : []).find((pipeline) => String(pipeline.id) === String(pipelineId));
        setPipelineName(match?.name || "");
      })
      .catch(() => setPipelineName(""));
  }, [pipelineId, workspaceId]);

  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [importer.step]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const closeOnEscape = (event) => {
      if (event.key === "Escape" && !importer.importing) onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [importer.importing, onClose]);

  const handleImport = async () => {
    const summary = await importer.runImport();
    if (summary) onImported?.(summary);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#071c2a]/55 p-3 backdrop-blur-sm sm:p-6" role="presentation">
      <section role="dialog" aria-modal="true" aria-labelledby="import-leads-title" className="flex max-h-[94vh] w-full max-w-5xl flex-col overflow-hidden rounded-[24px] border border-white/80 bg-white shadow-[0_30px_90px_-30px_rgba(3,29,45,0.65)]">
        <header className="flex items-center justify-between gap-4 px-6 py-5 sm:px-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#1689c1]">Lead import</p>
            <h2 id="import-leads-title" className="mt-1 text-xl font-bold tracking-tight text-[#102f44]">Import leads into your CRM</h2>
          </div>
          <button type="button" aria-label="Close import" onClick={onClose} disabled={importer.importing} className="flex h-10 w-10 items-center justify-center rounded-xl text-[#637e91] transition hover:bg-[#edf5f8] hover:text-[#17384e] disabled:opacity-40">
            <X className="h-5 w-5" />
          </button>
        </header>

        <StepHeader current={importer.step} />

        <div ref={contentRef} className="min-h-0 flex-1 overflow-y-auto px-6 py-6 sm:px-8 sm:py-7">
          {importer.error && (
            <div className="mb-5 flex items-start gap-3 rounded-xl border border-[#f2c8c4] bg-[#fff7f6] px-4 py-3 text-sm text-[#963f37]" role="alert">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{importer.error}</span>
            </div>
          )}

          {!starterFields ? <p role={schemaError ? "alert" : "status"} className="text-sm text-[#637e91]">{schemaError || "Loading pipeline fields…"}</p> : importer.step === "source" && <SourceStep loading={importer.loading} loadFile={importer.loadFile} loadGoogleSheet={importer.loadGoogleSheet} loadPaste={importer.loadPaste} />}
          {importer.step === "map" && <MappingStep starterFields={starterFields} {...importer} customFields={customFields} pipelineName={pipelineName} setFieldMapping={importer.setFieldMapping} />}
          {importer.step === "result" && <ResultStep result={importer.result} />}
        </div>

        {importer.step !== "source" && (
          <footer className="flex items-center justify-between gap-3 border-t border-[#dcebf3] bg-[#fbfdfe] px-6 py-4 sm:px-8">
            {importer.step === "map" ? (
              <button type="button" onClick={() => importer.setStep("source")} disabled={importer.importing} className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-[#516f82] transition hover:bg-[#edf5f8]">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            ) : <span />}

            {importer.step === "map" ? (
              <button type="button" onClick={handleImport} disabled={importer.importing || !importer.mapping.name || missingRequiredCustomField || missingRequiredStarterField} className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-b from-[#1689c1] to-[#106a99] px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50">
                {importer.importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                {importer.importing
                  ? "Importing leads…"
                  : `Import ${importer.rows.length.toLocaleString()} ${importer.rows.length === 1 ? "lead" : "leads"}`}
              </button>
            ) : (
              <button type="button" onClick={onClose} className="rounded-xl bg-[#147db5] px-6 py-2.5 text-sm font-bold text-white transition hover:bg-[#106a99]">Done</button>
            )}
          </footer>
        )}
      </section>
    </div>
  );
}
